import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Check, Plus, Search } from 'lucide-react';
import { z } from 'zod';
import { Button, Card, Field, Input, Modal } from '@/ui/primitives';
import { PageShell, Stack } from '@/ui/layout';
import { api, ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import {
  DetailDrawer,
  KanbanBoard,
  useKanban,
} from '@/modules/kanban';
import { FUNDIARIO_PIPES, TANARU_PIPE_ID } from '@/modules/fundiario/data';
import { TanaruMapa } from '@/modules/fundiario/components/TanaruMapa';
import { TANARU_META } from '@/modules/fundiario/tanaru';
import { useBuscarClientes, useImoveisDoCliente, type ClienteBusca } from '@/modules/ambiental/api';
import {
  fundiarioCardFieldMap,
  fundiarioDetailFieldMap,
} from '@/modules/fundiario/fieldMap';

function useCreateTriagemGeo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      cliente_cpf: string;
      imovel_id: number;
      municipio: string | null;
      area_estimada_ha: number | null;
      observacoes: string | null;
    }) => api('/api/v2/processos-geo', {
      method: 'POST',
      body: { tipo_processo: 'triagem_geo', ...body },
      schema: z.record(z.unknown()),
    }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['kanban', 'fundiario', 'triagem_geo'] });
    },
  });
}

function NovaTriagemGeoModal({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const create = useCreateTriagemGeo();
  const [busca, setBusca] = useState('');
  const [cliente, setCliente] = useState<ClienteBusca | null>(null);
  const [imovelId, setImovelId] = useState<number | null>(null);
  const [observacoes, setObservacoes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const buscaQ = useBuscarClientes(cliente ? '' : busca);
  const imoveisQ = useImoveisDoCliente(cliente?.cpf ?? null);
  const imoveis = imoveisQ.data ?? [];

  useEffect(() => {
    if (!open) return;
    setBusca('');
    setCliente(null);
    setImovelId(null);
    setObservacoes('');
    setError(null);
  }, [open]);

  async function handleSubmit() {
    setError(null);
    if (!cliente) return setError('Selecione o cliente.');
    const imovel = imoveis.find((item) => item.id === imovelId);
    if (!imovel) return setError('Selecione o imóvel que será triado.');
    try {
      await create.mutateAsync({
        cliente_cpf: cliente.cpf,
        imovel_id: imovel.id,
        municipio: imovel.municipio ?? cliente.municipio ?? null,
        area_estimada_ha: imovel.area_total_ha ?? null,
        observacoes: observacoes.trim() || null,
      });
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao criar Triagem Fundiário.');
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Nova Triagem Fundiário"
      size="lg"
      footer={(
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? 'Criando…' : 'Criar triagem'}
          </Button>
        </>
      )}
    >
      <Stack gap="lg">
        <Stack gap="sm">
          <p className="text-[0.68rem] font-medium uppercase tracking-[0.08em] text-text-muted">Cliente</p>
          {cliente ? (
            <button
              type="button"
              onClick={() => { setCliente(null); setImovelId(null); }}
              className="flex w-full items-center gap-2.5 rounded-[12px] bg-[var(--pipe-active)] p-2.5 text-left"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-text">{cliente.nome}</span>
                <span className="block text-xs text-text-muted">{cliente.cpf}</span>
              </span>
              <Check size={15} className="text-[var(--pipe-chip-fg)]" />
            </button>
          ) : (
            <>
              <div className="relative">
                <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por nome ou CPF" className="pl-9" />
              </div>
              {busca.trim().length >= 2 && (
                <div className="flex flex-col gap-1">
                  {buscaQ.data?.slice(0, 6).map((item) => (
                    <button key={item.cpf} type="button" onClick={() => setCliente(item)} className="rounded-[10px] bg-surface2 p-2.5 text-left hover:bg-surface3">
                      <span className="block text-sm font-medium text-text">{item.nome}</span>
                      <span className="block text-xs text-text-muted">{[item.cpf, item.municipio].filter(Boolean).join(' · ')}</span>
                    </button>
                  ))}
                  {buscaQ.data?.length === 0 && <p className="text-xs text-text-muted">Nenhum cliente encontrado.</p>}
                </div>
              )}
            </>
          )}
        </Stack>

        {cliente && (
          <Stack gap="sm">
            <p className="text-[0.68rem] font-medium uppercase tracking-[0.08em] text-text-muted">Imóvel</p>
            {imoveisQ.isLoading ? <p className="text-xs text-text-muted">Carregando imóveis…</p> : imoveis.map((imovel) => {
              const selected = imovel.id === imovelId;
              return (
                <button
                  key={imovel.id}
                  type="button"
                  onClick={() => setImovelId(imovel.id)}
                  className={cn('rounded-[10px] p-2.5 text-left', selected ? 'bg-[var(--pipe-active)]' : 'bg-surface2 hover:bg-surface3')}
                >
                  <span className="block text-sm font-medium text-text">{imovel.denominacao ?? `Imóvel ${imovel.id}`}</span>
                  <span className="block text-xs text-text-muted">
                    {[imovel.matricula && `Matrícula ${imovel.matricula}`, imovel.municipio, imovel.area_total_ha != null && `${imovel.area_total_ha} ha`].filter(Boolean).join(' · ')}
                  </span>
                </button>
              );
            })}
            {!imoveisQ.isLoading && imoveis.length === 0 && <p className="text-xs text-danger">O cliente precisa ter um imóvel cadastrado para abrir a triagem.</p>}
          </Stack>
        )}

        <Field label="Contexto inicial" htmlFor="triagem-geo-observacoes">
          <textarea
            id="triagem-geo-observacoes"
            rows={3}
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            className="resize-none rounded-[10px] bg-surface2 px-3 py-2.5 text-sm text-text outline-none focus:ring-2 focus:ring-accent-soft"
            placeholder="Motivo da demanda, urgência e informações já levantadas."
          />
        </Field>
        {error && <p className="text-sm text-danger">{error}</p>}
      </Stack>
    </Modal>
  );
}

function PipeSelectorCard({
  pipes,
  selected,
  onSelect,
}: {
  pipes: typeof FUNDIARIO_PIPES;
  selected: string | null;
  onSelect: (pipeId: string) => void;
}) {
  return (
    <Card padding="md" className="border-0 bg-surface2 shadow-none">
      <div className="flex flex-col gap-4">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-text-muted">Selecionar pipe</p>
        <div className="flex flex-wrap gap-3.5">
          {pipes.map((pipe) => {
            const Icon = pipe.icon;
            const isSelected = selected === pipe.id;
            return (
              <button
                key={pipe.id}
                type="button"
                onClick={() => onSelect(pipe.id)}
                className={cn(
                  'flex h-[77px] w-[92px] flex-col items-start gap-[7px] rounded-[7px] p-[9px] text-left transition-colors',
                  isSelected
                    ? 'bg-[var(--pipe-active)]'
                    : 'bg-surface shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:bg-[var(--pipe-active)] hover:shadow-none',
                )}
              >
                <span className="flex h-[27px] w-[27px] items-center justify-center rounded-[7px] bg-[var(--accent)]">
                  <Icon size={13} strokeWidth={1.6} className="text-[var(--on-solid)]" />
                </span>
                <span className="line-clamp-2 text-[0.82rem] font-semibold leading-tight text-text">{pipe.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

export const Route = createFileRoute('/_authed/geo')({
  validateSearch: (search: Record<string, unknown>) => ({
    card: typeof search.card === 'string' && /^\d+$/.test(search.card) ? search.card : undefined,
    pipe: typeof search.pipe === 'string' && FUNDIARIO_PIPES.some((item) => item.id === search.pipe)
      ? search.pipe
      : undefined,
  }),
  component: GeoPage,
});

function GeoPage() {
  const search = Route.useSearch();
  const [pipe, setPipe] = useState<string>(search.pipe ?? 'triagem_geo');
  const [selectedCard, setSelectedCard] = useState<string | null>(search.card ?? null);
  const [novaTriagemOpen, setNovaTriagemOpen] = useState(false);
  useEffect(() => {
    if (!search.card) return;
    setPipe(search.pipe ?? 'triagem_geo');
    setSelectedCard(search.card);
  }, [search.card, search.pipe]);
  const isTanaru = pipe === TANARU_PIPE_ID;
  // Pipe de mapa não tem kanban no banco — não busca
  const kanbanPipe = isTanaru ? undefined : pipe;
  const { data, isLoading, error } = useKanban('fundiario', kanbanPipe);

  return (
    <PageShell
      title="Fundiário"
      titleSize="sm"
      headerClassName="!pt-4"
      actions={(
        <Button variant="primary" onClick={() => setNovaTriagemOpen(true)}>
          <Plus size={15} /> Nova Triagem Fundiário
        </Button>
      )}
    >
      <Stack className="gap-5">
        <NovaTriagemGeoModal open={novaTriagemOpen} onOpenChange={setNovaTriagemOpen} />
        <PipeSelectorCard
          pipes={FUNDIARIO_PIPES}
          selected={pipe}
          onSelect={(id) => {
            setSelectedCard(null);
            setPipe(id);
          }}
        />
        {isTanaru ? (
          <Stack gap="sm">
            <p className="text-sm text-text-muted">
              {TANARU_META.nome} — {TANARU_META.decreto}, publicado no DOU de {TANARU_META.dou}.
            </p>
            <TanaruMapa />
          </Stack>
        ) : isLoading ? (
          <Card padding="lg">
            <p className="text-sm text-text-muted">Carregando kanban…</p>
          </Card>
        ) : error ? (
          <Card padding="lg">
            <p className="text-sm text-danger">
              {error instanceof ApiError ? error.message : 'Falha ao carregar kanban.'}
            </p>
          </Card>
        ) : data ? (
          <>
            <KanbanBoard
              data={data}
              fieldMap={fundiarioCardFieldMap}
              dominio="fundiario"
              pipe={pipe}
              bareSearch
              onCardSelect={setSelectedCard}
            />
            <DetailDrawer
              dominio="fundiario"
              pipe={pipe}
              cardId={selectedCard}
              columns={data.colunas}
              cardFieldMap={fundiarioCardFieldMap}
              detailFieldMap={fundiarioDetailFieldMap}
              onClose={() => setSelectedCard(null)}
            />
          </>
        ) : null}
      </Stack>
    </PageShell>
  );
}
